import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";

import { isAuthorized } from "@/core/auth/auth";
import { isAgelessDomain } from "@/core/host/isAgelessDomain";
import { useProductSubscriptions } from "@/modules/productSubscriptions";
import AgelessLoginBackgroundImage from "assets/ageless-login-bg.webp";
import LoginBackgroundImage from "assets/login-background.png";

export enum ThemeName {
  RepeatMD = "repeatmd",
  Ageless = "ageless",
  Neutral = "neutral",
}

export const THEME_NAMES = [ThemeName.RepeatMD, ThemeName.Ageless, ThemeName.Neutral] as const;

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const { subscriptions } = useProductSubscriptions();
  const isAuthed = isAuthorized();

  const pathname = useLocation().pathname;
  const isAdmin = pathname.startsWith("/admin");

  const themeName = useMemo<ThemeName>(() => {
    if (!isAuthed || !isAdmin) {
      return isAgelessDomain() ? ThemeName.Ageless : ThemeName.RepeatMD;
    }
    if (subscriptions.includes("AgelessAi") && subscriptions.includes("RepeatMD")) {
      return ThemeName.RepeatMD;
    }
    if (subscriptions.includes("AgelessAi")) {
      return ThemeName.Ageless;
    }
    return ThemeName.RepeatMD;
  }, [subscriptions, isAuthed, isAdmin]);

  const loginBgImage = useMemo(
    () =>
      themeName === ThemeName.Ageless
        ? `url(${AgelessLoginBackgroundImage})`
        : `url(${LoginBackgroundImage})`,
    [themeName],
  );

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = themeName;
    root.style.setProperty("--login-background-image", loginBgImage);
  }, [themeName, loginBgImage]);

  return children;
};
